import type { Asset, WorkOrderTemplate } from '#/graphql/types';
import { NuvoBox } from '@nuvolo/nuux-core/components/NuvoBox';
import React, { useCallback, useMemo, useState } from 'react';
import { NuvoGrid } from '@nuvolo/nuux-core/components/NuvoGrid';
import { NuvoText } from '@nuvolo/nuux-core/components/NuvoText';
import { useNavigate } from 'react-router-dom';
import Item from '#/app/components/TemplatePicker/Item';
import AssetPanel from './AssetPanel';
import RecordPreview, { RecordItem } from '#/common/components/RecordPreview';
import TemplatesList from './TemplatesList';
import useWOTemplateForAsset from '#/modules/wo-template/hooks/useWOTemplateForAsset';
import useAssetQuery from '../../hooks/useAssetQuery';
import styled from 'styled-components';
import useCreateWorkOrderByTemplateList from '../../hooks/useCreateWOByTemplatesMutation';
import useToast from '#/common/hooks/useToast';
import If from '#/common/components/If';
import EmptyMedium from '#/common/assets/empty-medium.svg';
import { color } from '@nuvolo/nuux-theme/utils';
import { useViewport } from '@nuvolo/nuux-theme/hooks/useViewport';
import TMMButton from '#/common/components/TMMButton';
import { NuvoLabel } from '@nuvolo/nuux-core/components/NuvoLabel';
import { StyledNuvoBox } from '#/common/styles/template';
import StandardThrobber from '#/app/components/Throbbers/StandardThrobber';
import { PageAction, PageActions } from '#/common/components/SubPage';
import FAIcon from '#/common/components/FAIcon';
import { executionIntervals } from '#/modules/wo-template/utils/constants';
import { Impact, impacts, priorities, Priority, types } from '#/config/mappings/mappings';

export interface TemplatePickerProps {
  assetId?: string;
}

const EmptyStateStyled = styled.div`
  height: fit-content;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 20px;
  background-color: ${color('custom', 'formGroupBackground')};
  border-radius: 4px;
`;

const NoTemplatesFoundText = styled.div`
  font-size: 14px;
  font-weight: 800;
  width: 150px;
  text-align: center;
  margin: 20px 0;
`;

const GoBackText = styled.div`
  color: ${color('secondary', 'dark2')};
  font-size: 16px;
  font-weight: 600;
  width: 150px;
  text-align: center;

  &:hover {
    text-decoration: underline;
    cursor: pointer;
  }
`;

const Img = styled.img`
  max-width: 110px;
  max-height: 100px;
  object-fit: contain;
`;

const getTemplatePreviewFields = (
  template: WorkOrderTemplate | undefined,
): RecordItem<WorkOrderTemplate>[] => [
  {
    items: [
      {
        dataField: 'executionInterval',
        label: 'Execution Interval',
        dataType: 'string',
        gridOptions: { xs: 12, sm: 6 },
        render: () => (
          <NuvoText type="body" bold>
            {executionIntervals.find((x) => x.key === template?.executionInterval)?.label || '-'}
          </NuvoText>
        ),
      },
      {
        dataField: 'title',
        label: 'Title',
        dataType: 'string',
        gridOptions: { xs: 12, sm: 6 },
      },
      {
        dataField: 'description',
        label: 'Description',
        dataType: 'string',
        gridOptions: { xs: 12 },
      },
      {
        dataField: 'type',
        label: 'Type',
        dataType: 'string',
        gridOptions: { xs: 12, sm: 6 },
        render: () => (
          <NuvoText type="body" bold>
            {types[template?.type as keyof typeof types]?.label || '-'}
          </NuvoText>
        ),
      },
      {
        dataField: 'priority',
        label: 'Priority',
        dataType: 'string',
        gridOptions: { xs: 12, sm: 6 },
        render: () => (
          <NuvoText type="body" bold>
            {priorities[Number(template?.priority) as Priority]?.label || '-'}
          </NuvoText>
        ),
      },
      {
        dataField: 'impact',
        label: 'Impact',
        dataType: 'string',
        gridOptions: { xs: 12, sm: 6 },
        render: () => (
          <NuvoText type="body" bold>
            {impacts[Number(template?.impact) as Impact]?.label || '-'}
          </NuvoText>
        ),
      },
      {
        dataField: 'notes',
        label: 'Notes',
        dataType: 'string',
        gridOptions: { xs: 12 },
      },
    ],
  },
];

const TemplatePicker: React.FC<TemplatePickerProps> = ({ assetId = '' }) => {
  // Hooks
  const { isDesktop } = useViewport();
  const { createWorkOrdersByTemplateList } = useCreateWorkOrderByTemplateList();
  const { alert, confirm } = useToast();

  // - assets
  const assetQuery = useAssetQuery(assetId);
  const asset = assetQuery.data?.getAsset?.data as Asset | undefined;

  // wo templates
  const templateQuery = useWOTemplateForAsset(asset);
  const templates = templateQuery.data?.getWorkOrderTemplateList?.data as
    | WorkOrderTemplate[]
    | undefined;

  // State
  const [templateToPreview, setTemplateToPreview] = useState<WorkOrderTemplate>();
  const [selectedTemplates, setSelectedTemplates] = useState<WorkOrderTemplate[]>([]);

  const navigate = useNavigate();

  const navigateToAssets = useCallback(() => {
    navigate(`/assets/${assetId}`);
  }, [assetId]);

  const generateMaintenancePlan = useCallback(async () => {
    if (!asset?.id || !selectedTemplates?.length) {
      return;
    }

    const templateIds = selectedTemplates.filter((t) => !!t.id).map((t) => t.id) as string[];

    try {
      const sure = await confirm(
        'info',
        `Are you sure you want to create work orders from those templates?`,
      );

      if (!sure) {
        return;
      }

      const _ = await createWorkOrdersByTemplateList([asset.id], templateIds);

      void alert('success', 'Successfully created work orders from the selected templates.');
      navigate(`/assets/${asset.id}/work-orders?assetId=${asset.id}`);
    } catch (error: any) {
      alert('error', 'Error while creating work orders from the selected templates.');
      console.error(error?.message || '');
    }
  }, [asset?.id, navigate, selectedTemplates, createWorkOrdersByTemplateList]);

  const actions = useMemo(() => {
    const result: PageAction[] = [];

    if (!!asset?.id && !!templates?.length) {
      result.push({
        id: 'mp-next',
        type: 'button',
        alwaysShow: true,
        props: {
          type: 'submit',
          label: 'Next',
          disabled: !asset || !selectedTemplates?.length,
          onClick: generateMaintenancePlan,
        },
      });
    }

    return result;
  }, [asset, selectedTemplates, templates]);

  return (
    <StyledNuvoBox isDesktop={isDesktop}>
      <div className="section-label">
        <NuvoLabel>Asset Details</NuvoLabel>
      </div>

      <NuvoBox display="flex" flexDirection="column" gap={20} marginBottom={20} id="tmm-section">
        <NuvoBox backgroundColor={color('contrast', 'light')} id="selected-asset-card">
          <AssetPanel asset={asset} />
        </NuvoBox>
      </NuvoBox>

      <div className="section-label">
        <NuvoLabel>Select Work Order Template</NuvoLabel>
      </div>
      <NuvoBox display="flex" flexDirection="column" gap={20} id="tmm-section">
        <If condition={templateQuery?.loading}>
          <StandardThrobber />
        </If>
        <If condition={!templateQuery?.loading}>
          <If condition={!!asset?.id && !!templates?.length}>
            <NuvoGrid container>
              {/* Select Template */}
              <NuvoGrid item xs={12} sm={6} className="tmm-section-panel">
                <NuvoBox
                  display="flex"
                  flexDirection="column"
                  gap={12}
                  backgroundColor={color('custom', 'dataGridBackground')}
                  paddingTop={4}
                  borderRadius="24px"
                  height="100%">
                  <TemplatesList
                    loading={templateQuery.loading}
                    asset={asset}
                    templates={templates}
                    previewTemplate={templateToPreview}
                    onSelect={setSelectedTemplates}
                    onSelectToPreview={setTemplateToPreview}
                  />
                </NuvoBox>
              </NuvoGrid>

              {/* Preview */}
              <NuvoGrid item xs={12} sm={6} className="tmm-section-panel">
                <div style={{ marginBottom: '16px' }}>
                  <Item>
                    <NuvoBox display="flex" alignItems="center" gap={8}>
                      <div
                        className="icon-wrapper"
                        style={{
                          padding: '8px',
                        }}>
                        <FAIcon color={color('custom', 'constantIconBlue')} icon="eye" size="xl" />
                      </div>
                      <span className="text-18">Preview</span>
                    </NuvoBox>
                    {templateToPreview ? (
                      <RecordPreview
                        record={templateToPreview}
                        fields={getTemplatePreviewFields(templateToPreview)}
                        noBorder
                        noPadding
                      />
                    ) : (
                      <NuvoBox display="flex" justifyContent="center" alignItems="center">
                        <div
                          style={{
                            width: '150px',
                            height: '274px',
                            textAlign: 'center',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}>
                          <NuvoBox display="flex" flexDirection="column" gap={16}>
                            <FAIcon icon="eye" />
                            <NuvoText type="large">
                              Click the eye icon to preview a template
                            </NuvoText>
                          </NuvoBox>
                        </div>
                      </NuvoBox>
                    )}
                  </Item>
                </div>
              </NuvoGrid>
            </NuvoGrid>
          </If>

          <If condition={!asset?.id || !templates?.length}>
            <EmptyStateStyled>
              <Img src={EmptyMedium} />
              <NoTemplatesFoundText>No templates found for this asset</NoTemplatesFoundText>
              <GoBackText onClick={navigateToAssets}>Go Back To Asset</GoBackText>
            </EmptyStateStyled>
          </If>
        </If>
      </NuvoBox>

      <PageActions actions={actions} />
    </StyledNuvoBox>
  );
};

export default React.memo(TemplatePicker);
